/**
 * Shared front-end resources: button styles, the animated logo, standard sounds.
 */
import type { Font } from '../assets/font';
import type { Assets } from '../assets/store';
import { TextColor } from '../assets/store';
import type { AudioEngine } from '../audio/audio';
import type { Sprite } from '../gfx/atlas';
import type { Renderer } from '../gfx/renderer';
import { Button, type ButtonStyle } from './widgets';

export interface Kit {
  fonts: { exocet10: Font; exocet8: Font; font16: Font; font24: Font; font30: Font; font42: Font; formal10: Font; formal11: Font; formal12: Font; font8: Font; font6: Font; ridiculous: Font; chat: Font };
  styles: Record<'wide' | 'medium' | 'short' | 'tall' | 'cancel' | 'narrow', ButtonStyle>;
  logo: { fireL: Sprite[]; fireR: Sprite[]; blackL: Sprite[]; blackR: Sprite[] };
  textbox: Sprite[];
  click(): void;
}

let kit: Kit | null = null;

export async function loadKit(a: Assets, audio: AudioEngine): Promise<Kit> {
  if (kit) return kit;
  const [exocet10, exocet8, font16, font24, font30, font42, formal10, formal11, formal12, font8, font6, ridiculous, chat] = await Promise.all(
    ['fontexocet10', 'fontexocet8', 'font16', 'font24', 'font30', 'font42', 'fontformal10', 'fontformal11', 'fontformal12', 'font8', 'font6', 'fontridiculous', 'fontingamechat'].map((f) =>
      a.font(f),
    ),
  );
  const fe = 'data\\global\\ui\\FrontEnd\\';
  const style = async (path: string, segments: number, font: Font, extra: Partial<ButtonStyle> = {}): Promise<ButtonStyle> => ({
    sprites: await a.dc6(path),
    segments,
    font,
    textColor: TextColor.Black,
    ...extra,
  });
  const styles = {
    wide: await style(`${fe}WideButtonBlank.dc6`, 2, exocet10),
    medium: await style(`${fe}MediumButtonBlank.dc6`, 1, exocet10),
    // The thin 272×25 bar under Battle.net that names the gateway.
    narrow: await style(`${fe}NarrowButtonBlank.dc6`, 2, font16),
    cancel: await style(`${fe}CancelButtonBlank.dc6`, 1, exocet10),
    // 135×25: Credits and Cinematics on the main menu.
    short: await style('data\\global\\ui\\CharSelect\\ShortButtonBlank.dc6', 1, font16),
    tall: await style('data\\global\\ui\\CharSelect\\TallButtonBlank.dc6', 1, exocet10),
  };
  const logo = {
    fireL: await a.dc6(`${fe}D2logoFireLeft.DC6`),
    fireR: await a.dc6(`${fe}D2logoFireRight.DC6`),
    blackL: await a.dc6(`${fe}D2logoBlackLeft.DC6`),
    blackR: await a.dc6(`${fe}D2logoBlackRight.DC6`),
  };
  kit = {
    fonts: { exocet10, exocet8, font16, font24, font30, font42, formal10, formal11, formal12, font8, font6, ridiculous, chat },
    styles,
    logo,
    textbox: await a.dc6(`${fe}textbox2.dc6`),
    click: () => void audio.playFile('data\\global\\sfx\\Cursor\\button.wav', { volume: 0.8 }),
  };
  return kit;
}

/** The animated Diablo II logo: black letters with additive fire, centered at (cx, y). */
export function drawLogo(r: Renderer, k: Kit, cx: number, y: number, t: number, alpha = 1): void {
  const f = Math.floor(t * 25);
  const pick = (s: Sprite[]) => s[f % s.length];
  r.draw(pick(k.logo.blackL), cx, y, { alpha });
  r.draw(pick(k.logo.blackR), cx, y, { alpha });
  r.draw(pick(k.logo.fireL), cx, y, { blend: 'add', alpha });
  r.draw(pick(k.logo.fireR), cx, y, { blend: 'add', alpha });
}

export function button(k: Kit, style: keyof Kit['styles'], x: number, y: number, label: string, onClick: () => void): Button {
  return new Button(x, y, k.styles[style], label.toUpperCase(), onClick, k.click);
}
